/**
 * Figma: "New Design system" (z9m9RfbUoacqyLimD9YUEb)
 * Page "RGB slider question" (11640:4195)
 * Default 11640:8441 · Empty-submit 11640:13879 · Mobile 11642:14046
 * Fail 11680:2353 · Success 11680:2498
 */
import React, { useEffect, useId, useRef, useState } from "react";
import { MessageDescriptor, useIntl } from "react-intl";

import TranslationsProvider from "@learningblocks/simulations/shared-components/lib/TranslationsProvider";
import { RichText } from "@learningblocks/shared-components/lib/RichText";

import { FeedbackBanner } from "../FeedbackBanner";
import { LxButton } from "../LxButton";
import { ActionAnnouncer } from "../ActionAnnouncer";
import { Slider } from "../Slider";

import { fetchTranslations } from "./locales/config";
import { messages } from "./displayMessages";
import {
  RgbChannel,
  RgbColor,
  RgbFeedback,
  RgbSliderQuestionProps,
} from "./types";
import {
  containerClass,
  footerClass,
  helperTextClass,
  promptGroupClass,
  questionCategoryClass,
  questionTextClass,
  rgbSliderQuestionClass,
  sliderEndLabelClass,
  sliderEndLabelsRowClass,
  sliderLabelClass,
  sliderLabelValueClass,
  sliderRowClass,
  sliderWrapperClass,
  slidersGroupClass,
  swatchClass,
  swatchColorClass,
  swatchLabelClass,
  swatchRowClass,
  visuallyHiddenClass,
} from "./RgbSliderQuestion.css";

export const RGB_DEFAULTS = {
  red: 255,
  green: 165,
  blue: 60,
  max: 255,
  min: 0,
  step: 15,
  dashCount: 18,
} as const;

export const DEFAULT_TARGET: RgbColor = {
  r: RGB_DEFAULTS.red,
  g: RGB_DEFAULTS.green,
  b: RGB_DEFAULTS.blue,
};

const CHANNEL_ORDER: RgbChannel[] = ["r", "g", "b"];

const CHANNEL_NAME: Record<RgbChannel, MessageDescriptor> = {
  r: messages.channelRed,
  g: messages.channelGreen,
  b: messages.channelBlue,
};

// The concrete message shape produced by `defineMessages` (required `id`),
// which is what `FeedbackBanner`'s `message` prop accepts — unlike the looser
// `MessageDescriptor`, whose `id` is optional.
type RgbMessage = (typeof messages)[keyof typeof messages];

const HINT_MESSAGES: Record<RgbChannel, Record<"more" | "less", RgbMessage>> = {
  r: { more: messages.addMoreRed, less: messages.addLessRed },
  g: { more: messages.addMoreGreen, less: messages.addLessGreen },
  b: { more: messages.addMoreBlue, less: messages.addLessBlue },
};

/**
 * Evaluate the learner's color against the target using the fixed
 * Red → Green → Blue priority order, surfacing a single directional hint for
 * the first mismatched channel. Returns a `warning` when no slider has moved.
 */
export function evaluateRgb(
  values: RgbColor,
  target: RgbColor,
  moved: boolean
): RgbFeedback {
  if (!moved) {
    return { variant: "warning" };
  }
  for (const channel of CHANNEL_ORDER) {
    if (values[channel] < target[channel]) {
      return { variant: "fail", channel, direction: "more" };
    }
    if (values[channel] > target[channel]) {
      return { variant: "fail", channel, direction: "less" };
    }
  }
  return { variant: "success" };
}

const RgbSliderQuestionInternal: React.FC<RgbSliderQuestionProps> = ({
  question,
  helperText,
  target = DEFAULT_TARGET,
  initialColor,
  isPredictionQuestion = false,
  explanation,
  onChange,
  onSubmit,
  onReset,
  setCompleted,
  testId,
}) => {
  const { formatMessage } = useIntl();

  const startColor = initialColor ?? { r: 0, g: 0, b: 0 };
  const [values, setValues] = useState<RgbColor>(startColor);
  const [moved, setMoved] = useState(
    () => startColor.r !== 0 || startColor.g !== 0 || startColor.b !== 0
  );
  const [feedback, setFeedback] = useState<RgbFeedback | null>(null);
  const [predictionSubmitted, setPredictionSubmitted] = useState(false);

  const componentId = useId();

  const isSolved = feedback?.variant === "success";
  const slidersDisabled = isPredictionQuestion ? predictionSubmitted : isSolved;

  // When the Submit button is swapped for its replacement ("Reset question" on
  // success, or "Reset prediction" after a prediction), move focus to the
  // replacement so keyboard/AT users are not dropped to <body> (WCAG 2.4.3).
  const footerRef = useRef<HTMLDivElement>(null);
  const showReplacement = isPredictionQuestion ? predictionSubmitted : isSolved;
  const prevShowReplacementRef = useRef(showReplacement);
  useEffect(() => {
    if (showReplacement && !prevShowReplacementRef.current) {
      footerRef.current?.querySelector("button")?.focus();
    }
    prevShowReplacementRef.current = showReplacement;
  }, [showReplacement]);

  // Label of the primary action button in its current state, fed to the
  // ActionAnnouncer so screen readers are told when Submit becomes Reset — on a
  // submitted prediction, or once the color is solved.
  const currentActionLabel = formatMessage(
    isPredictionQuestion
      ? predictionSubmitted
        ? messages.resetPrediction
        : messages.submitPrediction
      : isSolved
        ? messages.resetQuestion
        : messages.submitAnswer
  );

  const handleChannelChange = (channel: RgbChannel, value: number) => {
    const newValues = { ...values, [channel]: value };
    setValues(newValues);
    setMoved(true);
    // Clear a stale assessment banner so the learner re-submits after adjusting.
    if (!isPredictionQuestion && !isSolved) {
      setFeedback(null);
    }
    onChange?.(newValues);
  };

  const handleSubmit = () => {
    if (isPredictionQuestion) {
      setPredictionSubmitted(true);
      setCompleted?.(true);
      onSubmit?.(values, null);
      return;
    }

    const result = evaluateRgb(values, target, moved);
    setFeedback(result);
    if (result.variant === "success") {
      setCompleted?.(true);
    }
    onSubmit?.(values, result);
  };

  const handleReset = () => {
    setValues({ r: 0, g: 0, b: 0 });
    setMoved(false);
    setPredictionSubmitted(false);
    setFeedback(null);
    onReset?.();
  };

  const yourColorAnnouncement = formatMessage(messages.yourColorAnnouncement, {
    r: values.r,
    g: values.g,
    b: values.b,
  });

  const renderFeedback = () => {
    if (isPredictionQuestion) {
      if (!predictionSubmitted) return null;
      return (
        <FeedbackBanner
          variant="information"
          message={messages.predictionSubmitted}
        />
      );
    }
    if (!feedback) return null;
    if (feedback.variant === "warning") {
      return (
        <FeedbackBanner
          variant="warning"
          message={messages.emptySubmitWarning}
        />
      );
    }
    if (feedback.variant === "success") {
      // Verdict + explanation are composed here rather than being one ICU
      // message: the verdict is shipped copy, while the sentence after it is
      // authored per question (and already translated by the consumer). The
      // fallback interpolates `target`, so a question with no authored
      // explanation still describes the color the learner actually matched
      // instead of a hard-coded one.
      return (
        <FeedbackBanner
          variant="success"
          message={
            <>
              <RichText value={messages.successVerdict} addTagClasses={false} />{" "}
              <RichText
                value={
                  explanation ?? {
                    ...messages.targetColorFallback,
                    placeholderValues: {
                      r: target.r,
                      g: target.g,
                      b: target.b,
                    },
                  }
                }
                addTagClasses={false}
              />
            </>
          }
        />
      );
    }
    // fail: directional hint for the first mismatched channel
    const channel = feedback.channel as RgbChannel;
    const direction = feedback.direction as "more" | "less";
    return (
      <FeedbackBanner
        variant="fail"
        message={HINT_MESSAGES[channel][direction]}
      />
    );
  };

  const renderFooter = () => {
    if (isPredictionQuestion) {
      if (predictionSubmitted) {
        return (
          <LxButton
            variant="secondary"
            size="large"
            text={formatMessage(messages.resetPrediction)}
            onClickHandler={handleReset}
          />
        );
      }
      return (
        <LxButton
          variant="secondary"
          size="large"
          text={formatMessage(messages.submitPrediction)}
          onClickHandler={handleSubmit}
        />
      );
    }

    // Matches the other question types: once the answer is locked in, the
    // primary action becomes a secondary-variant reset.
    if (isSolved) {
      return (
        <LxButton
          variant="secondary"
          size="large"
          text={formatMessage(messages.resetQuestion)}
          onClickHandler={handleReset}
        />
      );
    }

    return (
      <LxButton
        variant="primary"
        size="large"
        text={formatMessage(messages.submitAnswer)}
        onClickHandler={handleSubmit}
      />
    );
  };

  const feedbackContent = renderFeedback();

  return (
    <div
      className={rgbSliderQuestionClass}
      data-name="RGB slider question"
      data-testid={testId}
    >
      <div className={containerClass}>
        <div className={promptGroupClass}>
          {isPredictionQuestion && (
            <p className={questionCategoryClass}>
              <RichText value={messages.predictionHeader} />
            </p>
          )}
          <p className={questionTextClass}>
            <RichText value={question ?? messages.defaultQuestion} />
          </p>
          <p className={helperTextClass}>
            <RichText value={helperText ?? messages.instructionText} />
          </p>
        </div>

        <div className={swatchRowClass} data-name="Swatches">
          <div
            className={swatchClass}
            role="img"
            aria-label={formatMessage(messages.targetColorSwatchLabel, {
              r: target.r,
              g: target.g,
              b: target.b,
            })}
          >
            <div
              className={swatchColorClass}
              style={{
                backgroundColor: `rgb(${target.r}, ${target.g}, ${target.b})`,
              }}
              data-testid="target-swatch"
            />
            <p className={swatchLabelClass}>
              <RichText value={messages.targetColorLabel} />
            </p>
          </div>
          <div
            className={swatchClass}
            role="img"
            aria-label={formatMessage(messages.yourColorSwatchLabel, {
              r: values.r,
              g: values.g,
              b: values.b,
            })}
          >
            <div
              className={swatchColorClass}
              style={{
                backgroundColor: `rgb(${values.r}, ${values.g}, ${values.b})`,
              }}
              data-testid="your-swatch"
            />
            <p className={swatchLabelClass}>
              <RichText value={messages.yourColorLabel} />
            </p>
          </div>
        </div>

        <div className={slidersGroupClass} data-name="Sliders">
          {CHANNEL_ORDER.map((channel) => {
            const channelName = formatMessage(CHANNEL_NAME[channel]);
            const value = values[channel];
            const labelId = `${componentId}-${channel}-label`;
            return (
              <div className={sliderRowClass} key={channel}>
                <p className={sliderLabelClass} id={labelId}>
                  {formatMessage(messages.channelValueLabel, {
                    channel: channelName,
                    value,
                    b: (chunks: React.ReactNode[]) => (
                      <span className={sliderLabelValueClass}>{chunks}</span>
                    ),
                  })}
                </p>
                <div className={sliderWrapperClass}>
                  <Slider
                    value={value}
                    min={RGB_DEFAULTS.min}
                    max={RGB_DEFAULTS.max}
                    step={RGB_DEFAULTS.step}
                    dashCount={RGB_DEFAULTS.dashCount}
                    interactive={!slidersDisabled}
                    ariaLabel={channelName}
                    ariaValueText={formatMessage(
                      messages.channelAriaValueText,
                      { value, max: RGB_DEFAULTS.max }
                    )}
                    onChange={(next) => handleChannelChange(channel, next)}
                    testId={`rgb-slider-${channel}`}
                  />
                </div>
                <div className={sliderEndLabelsRowClass}>
                  <span className={sliderEndLabelClass} aria-hidden="true">
                    {RGB_DEFAULTS.min}
                  </span>
                  <span className={sliderEndLabelClass} aria-hidden="true">
                    {RGB_DEFAULTS.max}
                  </span>
                </div>
              </div>
            );
          })}
        </div>

        <div className={visuallyHiddenClass} aria-live="polite" role="status">
          {yourColorAnnouncement}
        </div>

        {/*
          The banner is the single live region for the verdict. FeedbackBanner
          defaults to role="alert", which is announced reliably on insertion,
          and the verdict appears in direct response to Submit so assertive is
          the right register. It is deliberately not wrapped in a polite
          role="status" region: an assertive alert nested inside one is read
          twice, which costs real listening time now that the explanation is
          part of the same banner.
        */}
        {feedbackContent}

        <ActionAnnouncer label={currentActionLabel} />

        <div className={footerClass} data-name="Footer" ref={footerRef}>
          {renderFooter()}
        </div>
      </div>
    </div>
  );
};

export const RgbSliderQuestion = (props: RgbSliderQuestionProps) => (
  <TranslationsProvider fetchTranslations={fetchTranslations}>
    <RgbSliderQuestionInternal {...props} />
  </TranslationsProvider>
);

export * from "./types";
export { messages } from "./displayMessages";
